"use client";

import { Handshake } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MotionLinkButton } from "@/components/motion/MotionButton";
import { MotionIcon } from "@/components/motion/MotionIcon";
import { useScrollContext } from "@/components/motion/SmoothScrollProvider";
import { useMobileNavOpen } from "@/components/layout/MobileNavOpenContext";
import { useTheme } from "@/components/theme/ThemeProvider";
import { useNearFooter } from "@/hooks/useNearFooter";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

const SHOW_AFTER_SCROLL = 600;

export function StickyDonate() {
  const pathname = usePathname();
  const { scrollY } = useScrollContext();
  const nearFooter = useNearFooter(scrollY);
  const { theme } = useTheme();
  const { mobileNavOpen } = useMobileNavOpen();
  const reduced = useReducedMotion();

  const isHiddenRoute =
    pathname === "/donate" ||
    pathname.startsWith("/donate/") ||
    pathname === "/account" ||
    pathname.startsWith("/account/");
  const isVisible =
    !isHiddenRoute &&
    scrollY > SHOW_AFTER_SCROLL &&
    !nearFooter &&
    !mobileNavOpen;
  const isLight = theme === "light";

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduced ? undefined : { opacity: 0, y: 16 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-4 z-40 max-sm:right-3"
        >
          <MotionLinkButton href="/donate">
            <span
              className={cn(
                "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-colors sm:px-5 sm:py-3",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                isLight
                  ? "bg-[var(--programs-green-dark)] hover:bg-[var(--programs-green)]"
                  : "btn-accent bg-accent hover:bg-accent/90",
                !reduced && "donate-pulse"
              )}
            >
              <MotionIcon icon={Handshake} size={16} hoverScale={1.25} />
              Donate Now
            </span>
          </MotionLinkButton>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
